/**
 * While an HQ page shows a clinic dashboard, that dashboard's own requests go
 * to the HQ route that runs them at each chosen clinic and adds the answers
 * up. The dashboard is the clinic one, unchanged: its API functions still call
 * `analytics/dashboards/...`, and the request hook below rewrites the URL.
 *
 * Outside an HQ clinic report the scope is null and nothing is rewritten.
 *
 * No React here: api-config imports this, and server routes import api-config.
 * The hook is in `use-clinic-report-scope.ts`.
 */
export interface ClinicReportScope {
  /** Sub-domains; empty means every clinic in the enterprise. */
  clinics: string[];
  /** Every clinic HQ can pick, for the dashboard's Clinics filter. */
  options: readonly { label: string; value: string }[];
}

/** The group-by HQ adds to every grouped clinic report. */
export const CLINIC_GROUP_BY = "clinic";

const DASHBOARDS_PATH = "/analytics/dashboards/";
const CLINIC_REPORTS_PATH = "/enterprise/analytics/clinic-reports/dashboards/";

let scope: ClinicReportScope | null = null;
const listeners = new Set<() => void>();

export function setClinicReportScope(next: ClinicReportScope | null) {
  scope = next;
  listeners.forEach((listener) => listener());
}

export function getClinicReportScope(): ClinicReportScope | null {
  return scope;
}

export function subscribeToClinicReportScope(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A ky `beforeRequest` hook: sends a dashboard request to its HQ route while a scope is set. */
export function routeToClinicReports(request: Request): Request | undefined {
  if (!scope) return undefined;
  const url = new URL(request.url);
  const at = url.pathname.indexOf(DASHBOARDS_PATH);
  if (at === -1) return undefined;
  url.pathname =
    url.pathname.slice(0, at) +
    CLINIC_REPORTS_PATH +
    url.pathname.slice(at + DASHBOARDS_PATH.length);
  for (const clinic of scope.clinics)
    url.searchParams.append("clinics", clinic);
  return new Request(url, request);
}
